import type { ReactElement } from 'react'

import {
  Chip,
  Panel,
  SectionHeading,
  type ChipTone,
  type IconName,
} from '@maria-cavalheiro-website/shared-ui-web'

export interface AboutPageProps {
  readonly headingId: string
}

const TITLE = 'Maria Cavalheiro'
const EYEBROW = 'Sobre mim'
const BIOGRAPHY = [
  'Nasci no Rio Grande do Sul e hoje vivo em São Paulo.',
  'Tenho paixão pela natureza, pelas flores, pelos pássaros, pelos gatos e pela fotografia.',
] as const

const INTERESTS_TITLE = 'Do que gosto'
const INTERESTS: readonly { label: string; icon: IconName; tone: ChipTone }[] = [
  { label: 'Natureza', icon: 'leaf', tone: 'leaf' },
  { label: 'Flores', icon: 'flower', tone: 'mem' },
  { label: 'Pássaros', icon: 'bird', tone: 'bird' },
  { label: 'Gatos', icon: 'cat', tone: 'warm' },
]

/**
 * The biography, on a route of its own since phase 0020.
 *
 * IT OWNS THE `h1`, which is what makes it a document rather than a section. The
 * outline is two levels and no more:
 *
 *   h1  Maria Cavalheiro
 *     h2  Do que gosto
 *
 * The interests heading was an `h3` while this lived under the home document's
 * `h2`; promoting it is not cosmetic — an `h1` followed by an `h3` skips a level,
 * which both the unit suite and the E2E heading sweep fail on.
 *
 * AN `<article>`, because that is what this is: a self-contained composition about
 * one subject, complete away from the page that carries it. The E2E landmark sweep
 * holds every indexable route to one, and a biography is the honest case for it —
 * unlike the wrapper `<div>` that would have satisfied the same assertion.
 *
 * The `about__facts` definition list and the "Fotografia" interest chip were both
 * removed in this phase at the author's request. The biography paragraph still
 * names photography, and it is her own sentence, so it stays as written.
 */
export function AboutPage({ headingId }: AboutPageProps): ReactElement {
  return (
    <div className="about-page">
      <article className="about" aria-labelledby={headingId}>
        <SectionHeading level={1} eyebrow={EYEBROW} title={TITLE} id={headingId} />

        {/* `repeat(auto-fit, minmax(min(280px, 100%), 1fr))` — intrinsically
            responsive, so it needs no media query at all. */}
        <Panel variant="surface" className="about__panel">
          <div className="about__biography">
            {BIOGRAPHY.map((paragraph) => (
              <p className="about__paragraph" key={paragraph}>
                {paragraph}
              </p>
            ))}
          </div>

          {/* `about__aside` went with the facts list it existed to stack against
              the chips. One child in a flex column is not a layout. */}
          <div className="about__interests">
            <h2 className="about__interests-title">{INTERESTS_TITLE}</h2>
            <div className="about__chips">
              {INTERESTS.map((interest) => (
                <Chip key={interest.label} tone={interest.tone} icon={interest.icon}>
                  {interest.label}
                </Chip>
              ))}
            </div>
          </div>
        </Panel>
      </article>
    </div>
  )
}
